import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import AIChat from "@/components/AIChat";
import SEO from "@/components/SEO";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchBlog } from "@/lib/api";

export default function Blog() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    fetchBlog(id)
      .then((p) => active && setPost(p))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    const openChat = () => setChatOpen(true);
    window.addEventListener("open-ai-chat", openChat);
    return () => window.removeEventListener("open-ai-chat", openChat);
  }, []);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[var(--vp-bg)]" data-testid="blog-not-found">
        <Navbar variant="solid" />
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <span className="vp-label block mb-3">404</span>
          <h1 className="font-serif-vp text-5xl mb-4">Story not found</h1>
          <p className="text-[var(--vp-muted)] mb-8">This article has drifted off-course. Head back to the Journal for more.</p>
          <button onClick={() => navigate("/#journal")} className="text-[var(--vp-secondary)] underline underline-offset-4">Back to Journal</button>
        </div>
        <Footer />
      </div>
    );
  }

  const paragraphs = (post?.body || "").split(/\n\n+/).filter(Boolean);

  return (
    <div className="min-h-screen bg-[var(--vp-bg)]" data-testid="blog-page">
      {post && (
        <SEO
          title={`${post.title} | Vacations Pro Journal`}
          description={post.excerpt}
          path={`/blog/${post.id}`}
          image={post.image}
          schema={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: post.image,
            datePublished: post.published,
            author: { "@type": "Organization", name: post.author || "Vacations Pro" },
            publisher: {
              "@type": "Organization",
              name: "Vacations Pro",
              logo: { "@type": "ImageObject", url: "/logo.png" },
            },
            articleSection: post.category,
          }}
        />
      )}
      <Navbar variant="solid" />

      {/* Hero */}
      {loading ? (
        <Skeleton className="h-[480px] w-full" />
      ) : post && (
        <section className="relative h-[420px] md:h-[520px] overflow-hidden" data-testid="blog-hero">
          <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
          <div className="relative z-10 max-w-4xl mx-auto h-full flex flex-col justify-end px-6 md:px-10 pb-14 text-white">
            <Link to="/#journal" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-xs tracking-[0.22em] uppercase mb-5 w-max" data-testid="blog-back-link">
              <ArrowLeft className="w-3.5 h-3.5" /> The Journal
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="vp-label text-white/95">{post.category}</span>
              <span className="text-white/60">·</span>
              <span className="flex items-center gap-1.5 text-xs text-white/80 tracking-wider">
                <Clock className="w-3 h-3" />
                {post.read_time}
              </span>
            </div>
            <h1 className="font-serif-vp text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl" data-testid="blog-title">
              {post.title}
            </h1>
            <p className="text-white/85 mt-5 max-w-2xl text-base md:text-lg leading-relaxed">{post.excerpt}</p>
          </div>
        </section>
      )}

      {/* Body */}
      <section className="max-w-2xl mx-auto px-6 md:px-8 py-16 md:py-24">
        {loading ? (
          <div className="space-y-4">
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-5 w-full" />)}
          </div>
        ) : post && (
          <article className="space-y-6 text-[var(--vp-primary)] font-sans-vp text-[17px] leading-[1.75]" data-testid="blog-body">
            <div className="flex items-center gap-3 text-xs tracking-wider uppercase text-[var(--vp-muted)] border-b border-[var(--vp-border)] pb-6 mb-2">
              <span>{post.author || "The Vacations Pro Desk"}</span>
              <span>·</span>
              <span>{post.published || ""}</span>
            </div>
            {paragraphs.length === 0 ? (
              <p className="text-[var(--vp-muted)] italic">This article is being written. Check back soon, or ask our concierge directly.</p>
            ) : (
              paragraphs.map((p, idx) => (
                <p key={idx} className="first-letter:font-serif-vp first-letter:text-5xl first-letter:mr-2 first-letter:float-left first-letter:leading-[0.9] first-letter:text-[var(--vp-primary)]" style={idx === 0 ? {} : { textIndent: 0 }}>
                  {idx === 0 ? p : <span style={{ display: "block" }}>{p}</span>}
                </p>
              ))
            )}

            {/* CTA */}
            <div className="mt-14 p-8 bg-white border border-[var(--vp-border)] rounded-xl" data-testid="blog-cta">
              <span className="vp-label block mb-2 text-[var(--vp-secondary)]">CURATED FOR YOU</span>
              <h3 className="font-serif-vp text-2xl md:text-3xl leading-tight mb-3">
                Want a hand-made itinerary like this?
              </h3>
              <p className="text-[var(--vp-muted)] mb-5">
                Tell our concierge your dates and group size — we'll draft a tailored plan within 24 hours.
              </p>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("open-ai-chat"))}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--vp-primary)] text-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--vp-primary)]/90 transition"
                data-testid="blog-cta-btn"
              >
                <Sparkles className="w-4 h-4" /> Plan with our Concierge
              </button>
            </div>
          </article>
        )}
      </section>

      <Footer />
      <AIChat open={chatOpen} onOpenChange={setChatOpen} destination={post?.category?.toLowerCase?.()} />
      <WhatsAppFloat />
    </div>
  );
}
